import { Box, BoxProps, ButtonNew, ButtonProps, ColorOptions, Icon, Spacer, Text } from '@jfs/ui-new'
import { FC, useEffect } from 'react'
import { CircularProgress } from '../../comps/CircularProgress'
import { useLazyApiData, useStores } from '../../data/store'

interface UploadFileProgressProps {
  hide?: Boolean
  progress?: number
  fileName?: any
  onHandleCancelChangeFile?: any
  navToHistory?: any
  mid?: string
  file?: any
  resetState?: any
}

const UploadFileProgress: FC<UploadFileProgressProps> = ({
  hide = false,
  progress = 0,
  fileName,
  onHandleCancelChangeFile,
  navToHistory,
  mid,
  file,
  resetState,
}: UploadFileProgressProps) => {
  const {
    uploadBulkRefundFile,
    setFileUploadProgress,
    setUploadStatus,
    uploadStatus,
    count,
    size,
    getBulkRefundUploadConfirm,
    uploadedFileId,
  } = useStores((store) => ({
    uploadBulkRefundFile: store?.bulkRefund?.uploadBulkRefundFile,
    uploadStatus: store?.bulkRefund?.uploadStatus,
    setUploadStatus: store?.bulkRefund?.setUploadStatus,
    count: store?.bulkRefund?.count,
    size: store?.bulkRefund?.size,
    set: store?.bulkRefund?.size,
    setFileUploadProgress: store?.settings?.metaData?.setFileUploadProgress,
    getBulkRefundUploadConfirm: store?.bulkRefund?.getBulkRefundUploadConfirm,
    uploadedFileId: store?.bulkRefund?.uploadedFileId,
  }))

  const [uploadFile, uploadFileHandler] = useLazyApiData(uploadBulkRefundFile)
  const [uploadConfirm, uploadConfirmHandler] = useLazyApiData(getBulkRefundUploadConfirm)

  const btnStyle = uploadStatus === 'UPLOADED' ? styles.uploadButton : styles.proceedButton
  /** Upload File API Handler */
  useEffect(() => {
    if (uploadFileHandler?.state === 'fulfilled' && uploadFileHandler?.data?.status === 'FAILED') {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileHandler, uploadFileHandler.state, uploadFileHandler?.data?.status])

  useEffect(() => {
    if (uploadStatus !== 'UPLOADED' && uploadStatus !== 'PROCESSED') {
      uploadFile(mid, fileName, size, file)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, fileName, mid, size])

  useEffect(() => {
    if (uploadConfirmHandler?.state === 'fulfilled' && uploadConfirmHandler?.data?.status === 'CONFIRMED') {
      setUploadStatus('PROCESSED')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadConfirmHandler])

  if (hide) {
    return null
  }

  const uploadFileAndProcess = (_evt?: any) => {
    if (uploadStatus === 'UPLOADED') {
      uploadConfirm(uploadedFileId)
      setFileUploadProgress(0)
      setUploadStatus('IN_PROCESS')
      setTimeout(() => {
        setFileUploadProgress(100)
      }, 1000)
    } else {
      navToHistory()
    }
  }

  const getTitle = (type: 'cancel' | 'proceed', status: 'UPLOADED' | 'PROCESSED') => {
    const obj = {
      cancel: {
        UPLOADED: 'Cancel / Change File',
        PROCESSED: 'Upload a New File',
      },
      proceed: {
        UPLOADED: 'Upload File & Process Refund',
        PROCESSED: 'Check Status',
      },
    }
    return obj[type][status]
  }

  return (
    <Box {...styles.container}>
      <Box {...styles.content}>
        {(uploadStatus === 'IN_PROGRESS' || uploadStatus === 'IN_PROCESS') && (
          <CircularProgress percentage={progress}>
            <Box {...styles.progressView}>
              <Icon id={'UPLOAD'} color={'BLACK'} size={27} />
            </Box>
          </CircularProgress>
        )}
        {uploadStatus === 'UPLOADED' && (
          <Box {...styles.fileContainer}>
            <Icon color={'BLACK_21'} id={'SETTLEMENTS'} size={24} />
          </Box>
        )}
        {uploadStatus === 'PROCESSED' && (
          <Box {...styles.filledStyle}>
            <Icon id={'TICKMARK'} color={'WHITE'} size={27} />
          </Box>
        )}
        <Box {...styles.contentContainer}>
          <Text fontFamily={'jiotype-bold'} fontSize={14} color={'BLACK_21'} testID="bulkRefundFileUploadStatusMsg">
            {uploadStatus === 'IN_PROGRESS'
              ? 'Please wait while we are uploading your file for processing.'
              : uploadStatus === 'UPLOADED'
              ? fileName ?? ''
              : uploadStatus === 'IN_PROCESS'
              ? 'Please wait while we are uploading your file for processing'
              : uploadStatus === 'PROCESSED'
              ? 'File uploaded for processing refund'
              : null}
          </Text>
          <Spacer size={8} direction={'vertical'} />
          <Text fontFamily={'jiotype-medium'} fontSize={12} color={'PRIMARY_GREY'}>
            {uploadStatus === 'PROCESSED'
              ? 'You can check further status of refund and details in History section'
              : ''}
          </Text>
          <Box direction={'row'}>
            <Text fontFamily={'jiotype-medium'} fontSize={14} color={'BLACK_75'}>
              {uploadStatus === 'UPLOADED' ? 'Total Count : ' : ' '}
            </Text>
            <Text fontFamily={'jiotype-bold'} fontSize={14} color={'BLACK_21'}>
              {uploadStatus === 'UPLOADED' ? count : ' '}
            </Text>
          </Box>
        </Box>
        <Spacer size={32} />
        {(uploadStatus === 'UPLOADED' || uploadStatus === 'PROCESSED') && (
          <Box {...styles.buttonContainer}>
            <ButtonNew
              {...styles.cancelButton}
              title={getTitle('cancel', uploadStatus)}
              onPress={onHandleCancelChangeFile}
            />
            <Spacer size={24} direction={'horizontal'} />
            <ButtonNew
              testID={`uploadFileToProcess${uploadStatus}`}
              {...btnStyle}
              title={getTitle('proceed', uploadStatus)}
              onPress={uploadFileAndProcess}
            />
          </Box>
        )}
        {uploadStatus === 'IN_PROGRESS' && <Box height={32} />}
      </Box>
      <Spacer size={40} direction={'vertical'} />
    </Box>
  )
}

const styles = {
  container: { width: '100%', height: '70vh', alignItems: 'center' } as BoxProps,
  content: {
    height: '350px',
    width: '700px',
    borderWidth: 1,
    borderColor: 'DISABLED_GREY',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  } as BoxProps,
  progressView: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: 80,
    height: 80,
    borderRadius: 40,
    borderColor: '#3F8C3A33' as ColorOptions,
    alignItems: 'center',
    zIndex: 10000,
    justifyContent: 'center',
  } as BoxProps,
  filledStyle: {
    width: '80px',
    height: '80px',
    borderRadius: 40,
    borderColor: '#3F8C3A33' as ColorOptions,
    alignItems: 'center',
    zIndex: 10000,
    justifyContent: 'center',
    backgroundColor: '#3F8C3A' as ColorOptions,
  } as BoxProps,
  fileContainer: {
    width: '80px',
    height: '80px',
    borderRadius: 40,
    borderWidth: 1,
    borderColor: '#3F8C3A33' as ColorOptions,
    alignItems: 'center',
    backgroundColor: '#3F8C3A14' as ColorOptions,
    justifyContent: 'center',
  } as BoxProps,
  contentContainer: {
    direction: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
  } as BoxProps,
  buttonContainer: {
    direction: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  } as BoxProps,
  cancelButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'PRIMARY',
    type: 'secondarySmall',
  } as ButtonProps,
  proceedButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'WHITE',
    type: 'primaryIconSmall',
  } as ButtonProps,
  uploadButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'WHITE',
    type: 'primaryIconSmall',
    icon: 'UPLOAD',
  } as ButtonProps,
}
export { UploadFileProgress }
